import React, { useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  theme,
} from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import CertificacionEditorDrawer from 'components/admin/Certificaciones/CertificacionEditorDrawer';
import CertificacionBadge from 'components/admin/compliance/CertificacionBadge';
import useTenantFlags from 'hooks/useTenantFlags';
import {
  ESTADO_CERTIFICACION_COLORS,
  ESTADO_CERTIFICACION_LABELS,
  TIPO_CERTIFICACION_LABELS,
  useCertificaciones,
} from 'providers/Certificaciones';

const { Text } = Typography;
const { useToken } = theme;

// Lista de certificaciones de un equipo con CRUD inline. Se monta dentro del
// tab "Certificaciones" del EquipoDetailDrawer.
function CertificacionesList({ idEquipo }) {
  const { token } = useToken();
  const { readOnly, minvuEnabled } = useTenantFlags();
  const { certificaciones, loading, error, refresh, create, update, remove } =
    useCertificaciones(idEquipo);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const openCreate = () => {
    setEditTarget(null);
    setDrawerOpen(true);
  };
  const openEdit = (row) => {
    setEditTarget(row);
    setDrawerOpen(true);
  };
  const close = () => {
    setDrawerOpen(false);
    setEditTarget(null);
  };
  const handleSubmit = async (payload) => {
    if (editTarget?.id) {
      await update(editTarget.id, payload);
    } else {
      await create(payload);
    }
    close();
  };
  const handleDelete = async (row) => {
    try {
      await remove(row.id);
      message.success('Certificación eliminada');
    } catch (err) {
      message.error(err?.response?.data?.message || 'No se pudo eliminar');
    }
  };

  // MINVU off ⇒ el módulo de certificaciones no aplica para este tenant.
  if (!minvuEnabled) return null;

  const columns = [
    {
      title: 'Vencimiento',
      dataIndex: 'fecha_vencimiento',
      width: 150,
      render: (fecha) => <CertificacionBadge fecha={fecha} size="sm" />,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      width: 110,
      render: (t) => <Tag>{TIPO_CERTIFICACION_LABELS[t] ?? t}</Tag>,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      width: 110,
      render: (e) => (
        <Tag color={ESTADO_CERTIFICACION_COLORS[e] ?? 'default'}>
          {ESTADO_CERTIFICACION_LABELS[e] ?? e}
        </Tag>
      ),
    },
    {
      title: 'Entidad',
      dataIndex: 'entidad_certificadora',
      ellipsis: true,
    },
    {
      title: 'Emisión',
      dataIndex: 'fecha_emision',
      width: 110,
      render: (d) => (d ? dayjs(d).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Sello',
      dataIndex: 'sello_verde',
      width: 80,
      render: (v) =>
        v ? (
          <Tooltip title="Sello verde MINVU">
            <CheckOutlined style={{ color: token.colorSuccess }} />
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    ...(readOnly
      ? []
      : [
          {
            title: 'Acciones',
            key: 'actions',
            width: 120,
            render: (_, row) => (
              <Space size="small">
                <Tooltip title="Editar">
                  <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(row)} />
                </Tooltip>
                <Popconfirm
                  title="Eliminar certificación"
                  okText="Eliminar"
                  okType="danger"
                  cancelText="Cancelar"
                  onConfirm={() => handleDelete(row)}
                >
                  <Tooltip title="Eliminar">
                    <Button icon={<DeleteOutlined />} size="small" danger />
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          },
        ]),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginMD }}>
      <Space style={{ justifyContent: 'space-between', display: 'flex', width: '100%' }} wrap>
        <Space align="center">
          <SafetyCertificateOutlined style={{ color: token.colorPrimary }} />
          <Text strong>Histórico de certificaciones</Text>
        </Space>
        {!readOnly && (
          <Button type="primary" icon={<PlusOutlined />} size="small" onClick={openCreate}>
            Registrar nueva
          </Button>
        )}
      </Space>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar las certificaciones"
          action={
            <Button size="small" onClick={refresh}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={certificaciones}
          pagination={false}
          locale={{
            emptyText: (
              <Empty description="Este equipo todavía no tiene certificaciones registradas." />
            ),
          }}
        />
      )}

      <CertificacionEditorDrawer
        open={drawerOpen}
        certificacion={editTarget}
        onClose={close}
        onSubmit={handleSubmit}
      />
    </div>
  );
}

export default CertificacionesList;
